<template>
  <b-form
    v-if="initialised"
    @submit.prevent="onSubmit"
    @keydown="form.errors.clear($event.target.id)"
  >
    <div class="row">
      <div class="col-3">
        <b-card header="General">
          <div class="form-group row" :class="{ 'invalid-wrapper': form.errors.has('is_company') }">
            <div class="col-3 col-form-label text-right">Is Company</div>
            <div class="col-8">
              <core-switch
                v-model="form.is_company"
                variant="3d"
                color="primary"
                on="Y"
                off="N"
                @change="form.errors.clear('is_company')"
              />

              <small
                v-if="form.errors.has('is_company')"
                class="invalid-feedback"
                v-text="form.errors.get('is_company')"
              ></small>
            </div>
          </div>

          <div v-if="form.is_company">
            <form-select
              v-model="form.company_type"
              name="company_type"
              :options="companyTypes"
              @input="form.errors.clear()"
              >Type
            </form-select>
            <form-text v-model="form.company_name" name="company_name" @input="companyNameChanged">
              Name
            </form-text>
            <form-text v-model="form.abn" name="abn">ABN</form-text>

            <div
              class="form-group row"
              :class="{ 'invalid-wrapper': form.errors.has('is_branch') }"
            >
              <div class="col-3 col-form-label text-right">Is Branch</div>
              <div class="col-8">
                <core-switch
                  v-model="form.is_branch"
                  variant="3d"
                  color="primary"
                  on="Y"
                  off="N"
                  @change="form.errors.clear('is_branch')"
                />
                <small
                  v-if="form.errors.has('is_branch')"
                  class="invalid-feedback"
                  v-text="form.errors.get('is_branch')"
                ></small>
              </div>
            </div>
            <form-customer-select
              v-if="form.is_branch"
              v-model="form.parent_company"
              name="parent_company"
              type="companies"
              :show-reset-button="true"
              :show-extra-slot="true"
              error-id="general.parent_company"
              @change="customerChanged"
            />
          </div>

          <form-select
            v-model="form.payment_terms"
            name="payment_terms"
            :options="paymentTerms"
            @input="form.errors.clear()"
          />

          <div
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('payment_terms_approved') }"
          >
            <div class="col-3 col-form-label text-right">Payment Terms Approved</div>
            <div class="col-8">
              <core-switch
                v-model="form.payment_terms_approved"
                variant="3d"
                color="primary"
                on="Y"
                off="N"
                @change="form.errors.clear(['payment_terms', 'payment_terms_approved'])"
              />
              <small
                v-if="form.errors.has('payment_terms_approved')"
                class="invalid-feedback"
                v-text="form.errors.get('payment_terms_approved')"
              ></small>
            </div>
          </div>

          <div class="form-group row" :class="{ 'invalid-wrapper': form.errors.has('opt_in') }">
            <div class="col-3 col-form-label text-right">Opt In</div>
            <div class="col-8">
              <core-switch
                v-model="form.opt_in"
                variant="3d"
                color="primary"
                on="Y"
                off="N"
                @change="form.errors.clear(['payment_terms', 'opt_in'])"
              />
              <small
                v-if="form.errors.has('opt_in')"
                class="invalid-feedback"
                v-text="form.errors.get('opt_in')"
              ></small>
            </div>
          </div>

          <form-user-select
            v-model="form.picked_up_by_user"
            name="picked_up_by"
            placeholder="Search technician..."
            :show-reset-button="true"
            :show-extra-slot="true"
            @change="updatePickedUpBy"
          >
            Technician Booking
          </form-user-select>

          <form-text
            name="primary_contact"
            :value="$lodash.get(currentPrimaryContact, 'name')"
            :readonly="true"
          ></form-text>
          <form-text
            name="postal_address"
            :value="$lodash.get(currentPostalAddress, 'shortAddress')"
            :readonly="true"
          ></form-text>

          <form-textarea v-model="form.memo" name="memo" />
        </b-card>

        <b-card v-if="companyFuzzySearching || companyFuzzySearchResults.length > 0">
          <div v-if="companyFuzzySearching">
            <b-alert show variant="primary" class="mb-0">
              <font-awesome-icon spin :icon="['fal', 'spinner']" />
              Searching similar companies...
            </b-alert>
          </div>
          <div v-else-if="companyFuzzySearchResults.length > 0">
            <div class="pb-2">
              Found {{ companyFuzzySearchTotal }} similar
              {{ companyFuzzySearchResults.length > 1 ? "companies" : "company" }}
            </div>

            <ul class="list-group">
              <li
                v-for="(similarCompany, index) in companyFuzzySearchResults"
                :key="`similar-company-${index}`"
                class="list-group-item px-3 pt-3 pb-2"
              >
                <div class="row">
                  <div class="col d-flex">
                    <customer-object
                      :entity-id="similarCompany.id"
                      :name="similarCompany.company_name"
                      :open-new="true"
                      size="badge-xl"
                    />
                  </div>

                  <div class="col text-right">
                    <b-button
                      v-if="isModal"
                      size="sm"
                      variant="success"
                      @click.prevent="useThisCustomer(similarCompany)"
                    >
                      Use this Customer
                    </b-button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </b-card>
      </div>
      <div :class="crud.getAction() === 'create' ? 'col-9' : 'col-7'">
        <b-card no-body>
          <b-tabs ref="tabs" card>
            <b-tab title="Contacts" :title-item-class="errorTabClass('contacts')">
              <b-card>
                <table class="table table-sm table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Mobile</th>
                      <th>Primary</th>
                      <th style="width: 50px">Active</th>
                      <th style="width: 50px"></th>
                    </tr>
                  </thead>
                  <tbody v-for="(contact, index) in form.contacts" :key="`contact-${index}`">
                    <tr>
                      <td>
                        <input
                          v-model.trim="contact.first_name"
                          type="text"
                          class="form-control"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.first_name`),
                          }"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.first_name`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.first_name`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.last_name"
                          type="text"
                          class="form-control"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.last_name`),
                          }"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.last_name`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.last_name`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.email"
                          type="email"
                          class="form-control"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.email`),
                          }"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.email`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.email`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.phone"
                          type="text"
                          class="form-control"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.phone`),
                          }"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.phone`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.phone`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.mobile"
                          type="text"
                          class="form-control"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.mobile`),
                          }"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.mobile`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.mobile`)"
                        ></small>
                      </td>
                      <td>
                        <core-switch
                          variant="3d"
                          color="primary"
                          :model-checked="contact.is_primary"
                          @change="primaryContactChanged($event, index)"
                        />
                      </td>
                      <td>
                        <core-switch
                          v-model="contact.is_active"
                          variant="3d"
                          color="success"
                          :checked="contact.is_active"
                          @change="form.errors.clear(`contacts.${index}.is_active`)"
                        />
                      </td>
                      <td style="padding-top: 13px">
                        <font-awesome-icon
                          v-if="contact.is_creating"
                          :icon="['fal', 'times']"
                          class="cursor"
                          @click.stop="removeContact(index)"
                        />
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="8" class="invalid-wrapper">
                        <small
                          v-if="form.errors.has('contacts.primary_contact_id')"
                          class="invalid-feedback"
                          v-text="form.errors.get('contacts.primary_contact_id')"
                        ></small>
                        <div
                          v-for="(contact, index) in form.contacts"
                          :key="`contact-${index}-error`"
                        >
                          <small
                            v-if="form.errors.has(`contacts.${index}.is_active`)"
                            class="invalid-feedback"
                            v-text="form.errors.get(`contacts.${index}.is_active`)"
                          ></small>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="8">
                        <button
                          type="button"
                          class="btn btn-primary btn-sm"
                          @click.stop="addContact()"
                        >
                          <font-awesome-icon :icon="['fal', 'plus-circle']" />
                          Add Contact
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </b-card>

              <b-card v-if="contactsFuzzySearching || contactsFuzzySearchResults.length > 0">
                <div v-if="contactsFuzzySearching">
                  <b-alert show variant="primary" class="mb-0">
                    <font-awesome-icon spin :icon="['fal', 'spinner']" />
                    Searching similar customers...
                  </b-alert>
                </div>
                <div v-else-if="contactsFuzzySearchResults.length > 0">
                  <div class="pb-2">
                    Found {{ contactsFuzzySearchTotal }} similar
                    {{ contactsFuzzySearchResults.length > 1 ? "customers" : "customer" }}
                  </div>

                  <ul class="list-group">
                    <li
                      v-for="(similarCustomer, index) in contactsFuzzySearchResults"
                      :key="`similar-customer-${index}`"
                      class="list-group-item px-3 pt-3 pb-2"
                    >
                      <div class="row">
                        <div class="col d-flex">
                          <customer-object
                            :entity-id="similarCustomer.id"
                            :name="getCustomerFullNameTag(similarCustomer)"
                            :open-new="true"
                            size="badge-xl"
                          />
                        </div>
                        <div class="col text-right">
                          <b-button
                            v-if="
                              crud.getAction() === 'edit' && similarCustomer.id === crud.data.id
                            "
                            v-tooltip="{
                              content: 'This contact already exists, for this customer',
                              classes: ['badge-theme'],
                            }"
                            size="sm"
                            variant="danger"
                            class="mr-1"
                          >
                            Current Customer
                          </b-button>

                          <b-button
                            v-if="isModal"
                            size="sm"
                            variant="success"
                            @click.prevent="useThisCustomer(similarCustomer)"
                          >
                            Use this Customer
                          </b-button>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col">
                          <div class="text-muted text-13 pt-1">
                            {{ $lodash.get(similarCustomer, "postal_address.formatted") }}
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </b-card>
            </b-tab>

            <b-tab title="Addresses" :title-item-class="errorTabClass('addresses')">
              <b-card>
                <table class="table table-sm table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th style="width: 50px">Postal</th>
                      <th style="width: 50px">Active</th>
                      <th style="width: 50px"></th>
                    </tr>
                  </thead>
                  <tbody v-for="(address, index) in form.addresses" :key="`address-${index}`">
                    <tr>
                      <td>
                        <form-address
                          :name="`addresses.${index}`"
                          :address="address"
                          :single-row="false"
                          :replaceable="true"
                          :labelless="true"
                          :value="address"
                          bottom-margin="mb-0"
                          @updateAddress="addressChanged($event, index)"
                        />
                      </td>
                      <td>
                        <core-switch
                          variant="3d"
                          color="primary"
                          :model-checked="address.is_postal"
                          @change="postalAddressChanged($event, index)"
                        />
                      </td>
                      <td>
                        <core-switch
                          v-model="address.is_active"
                          variant="3d"
                          color="success"
                          :checked="address.is_active"
                          @change="form.errors.clear(`addresses.${index}.is_active`)"
                        />
                      </td>
                      <td style="padding-top: 13px">
                        <font-awesome-icon
                          v-if="address.is_creating"
                          :icon="['fal', 'times']"
                          class="cursor"
                          @click.stop="removeAddress(index)"
                        />
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="4" class="invalid-wrapper">
                        <small
                          v-if="form.errors.has('addresses.postal_address_id')"
                          class="invalid-feedback"
                          v-text="form.errors.get('addresses.postal_address_id')"
                        ></small>

                        <div
                          v-for="(address, index) in form.addresses"
                          :key="`address-${index}-error`"
                        >
                          <small
                            v-if="form.errors.has(`addresses.${index}.is_active`)"
                            class="invalid-feedback"
                            v-text="form.errors.get(`addresses.${index}.is_active`)"
                          ></small>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="4">
                        <button
                          type="button"
                          class="btn btn-primary btn-sm"
                          @click.stop="addAddress()"
                        >
                          <font-awesome-icon :icon="['fal', 'plus-circle']" />
                          Add Address
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </b-card>
            </b-tab>

            <b-tab v-if="crud.data.branches && crud.data.branches.length > 0" title="Branches">
              <div class="row">
                <div
                  v-for="(branch, index) in crud.data.branches"
                  :key="`branch-${branch.id}`"
                  class="col-6"
                >
                  <b-card>
                    <template #header>
                      <b>{{ branch.company_name }}</b> ({{ branch.company_type }})
                    </template>

                    <table class="table table-striped table-hover">
                      <tbody>
                        <tr>
                          <th style="width: 180px">ABN</th>
                          <td>{{ branch.abn }}</td>
                        </tr>
                        <tr>
                          <th>Primary Contact</th>
                          <td>{{ branch.primary_contact.name }}</td>
                        </tr>
                        <tr>
                          <th>Postal Address</th>
                          <td>{{ branch.postal_address.shortAddress }}</td>
                        </tr>
                        <tr>
                          <th>Payment Terms</th>
                          <td>{{ branch.payment_terms }}</td>
                        </tr>
                        <tr>
                          <th>Technician Booking</th>
                          <td>{{ $lodash.get(branch, "picked_up_by_user.name", "N/A") }}</td>
                        </tr>
                      </tbody>
                    </table>

                    <button class="btn btn-danger" @click.prevent="removeBranch(branch, index)">
                      Remove branch
                    </button>
                  </b-card>
                </div>
              </div>
            </b-tab>

            <b-tab v-if="crud.data.id" title="Activities">
              <activities :current-customer="crud.data" />
            </b-tab>
          </b-tabs>
        </b-card>
      </div>
      <div v-if="crud.getAction() !== 'create'" class="col-2">
        <b-card header="Jobs">
          <job-list
            :create-action="createJob"
            :goto-action="gotoJob"
            :query="{ customerId: $router.currentRoute.params.id }"
          />
        </b-card>
      </div>
    </div>

    <form-validation-alert :errors="form.errors" />

    <b-card-footer class="mb-5">
      <button class="btn btn-primary" :disabled="form.notReady()">
        <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
        <font-awesome-icon v-else :icon="['fal', 'check']" />
        {{ crud.getAction() === "create" ? "Create" : "Save" }}
      </button>
    </b-card-footer>
  </b-form>
</template>

<script>
import debounce from "debounce";
import ux from "../../mixins/ux";
import EventBus from "../../imports/event-bus";
import JobList from "../../components/common/JobList.vue";
import Activities from "./partials/Activities.vue";

export default {
  components: {
    Activities,
    JobList,
  },
  mixins: [ux],

  data() {
    return {
      isModal: false,

      companyTypes: [
        "Real Estate",
        "Builder",
        "Contract",
        "Commercial",
        "Retail Business",
        "Hospitality",
        "Health",
      ],
      paymentTerms: [
        "Cash on Delivery (COD)",
        "7 Days",
        "30 Days",
        "45 Days",
        "30 Days from End of Month",
      ],
      initialised: false,
      crud: null,
      form: new window.AppForm({
        opt_in: 0,
        is_company: 0,
        is_branch: 0,
        parent_company: "",
        company_name: "",
        company_type: "",
        abn: "",
        payment_terms: "",
        payment_terms_approved: 0,
        picked_up_by: "",
        picked_up_by_user: "",
        addresses: [],
        contacts: [],
        memo: "",
      }),
      contactsFuzzySearching: false,
      contactsFuzzySearchResults: [],
      contactsFuzzySearchTotal: null,
      companyFuzzySearching: false,
      companyFuzzySearchResults: [],
      companyFuzzySearchTotal: null,
    };
  },

  computed: {
    currentPrimaryContact() {
      return this.form.contacts.find((contact) => contact.is_primary);
    },
    currentPostalAddress() {
      return this.form.addresses.find((address) => address.is_postal);
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    init() {
      this.initialised = false;

      this.crud = new window.AppCrud("customer", [
        "primaryContact",
        "branches.primaryContact",
        "branches.postalAddress",
        "branches.pickedUpByUser",
      ]);

      this.crud.initFormData(this.form).then(() => {
        if (!this.form.payment_terms || this.form.payment_terms.length === 0) {
          this.form.payment_terms = "Cash on Delivery (COD)";
        }
        if (!this.form.company_type || this.form.company_type.length === 0) {
          this.form.company_type = "Real Estate";
        }

        // Initiate if company is branch
        if (this.form.is_company && this.$lodash.has(this.form, "parent_company.id")) {
          this.form.is_branch = true;
        }

        // Initiate contacts with is_primary
        this.form.contacts = this.form.contacts.map((contact) => ({
          ...contact,
          is_primary: contact.id === this.crud.data.primary_contact_id,
          is_active: contact.is_active === 1,
        }));

        // Initiate addresses with is_postal
        this.form.addresses = this.form.addresses.map((address) => ({
          ...address,
          is_postal: address.id === this.crud.data.postal_address_id,
          is_active: address.is_active === 1,
        }));

        // Auto populate default contact/address if it's create action
        if (this.crud.getAction() === "create") {
          this.addContact();
          this.addAddress();
        }

        // Reset fuzzy search
        this.contactsFuzzySearchResults = [];
        this.contactsFuzzySearchTotal = 0;
        this.contactsFuzzySearching = false;

        // Browser Title
        if (this.$lodash.get(this.crud, "data.primary_contact.name")) {
          // noinspection JSUnresolvedFunction
          this.$root.setTitle(
            `Customer - ${this.$lodash.get(this.crud, "data.primary_contact.name")}`
          );
        } else if (this.$lodash.get(this.crud, "data.id")) {
          // noinspection JSUnresolvedFunction
          this.$root.setTitle(`Customer - ${this.$lodash.get(this.crud, "data.id")}`);
        }

        this.initialised = true;
      });
    },

    createJob() {
      this.$store.dispatch("jobs/create", {
        newWindow: true,
        customer_id: this.crud.data.id,
      });
    },

    gotoJob(id) {
      window.open(`/jobs/${id}/edit`);
    },

    onSubmit() {
      this.crud.submit(this.form).then((response) => {
        if (this.crud.getAction() === "create") {
          window.toastr.success("Customer created!", "Success");
          this.$router.push(
            {
              name: "customers.edit",
              params: { id: response.id },
            },
            () => {
              this.init();
            }
          );
        } else {
          this.init();
          window.toastr.success("Customer updated!", "Success");
        }
      });
    },

    updatePickedUpBy(user) {
      if (user === null) {
        this.form.picked_up_by_user = null;
        this.form.picked_up_by = null;
      } else {
        this.form.picked_up_by_user = user;
        this.form.picked_up_by = user.id;
      }

      this.form.errors.clear();
    },

    customerChanged(customer) {
      if (customer === null) {
        this.form.parent_company = "";
        this.form.is_branch = false;
        return;
      }

      if (!this.form.parent_company || this.form.parent_company.id !== customer.id) {
        // Assign the customer to the newly selected
        this.form.parent_company = customer;
        this.form.parent_company.id = customer.id;

        this.form.errors.clear("general.parent_company");
      }
    },

    getCustomerFullNameTag(customer) {
      if (customer.is_company) {
        return `${customer.primary_contact.name} @ ${customer.company_name} (${customer.company_type})`;
      }

      return customer.primary_contact.name;
    },

    addContact() {
      this.form.contacts.push({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        mobile: "",
        is_primary: false,
        is_active: true,
        is_creating: true,
      });
    },

    removeContact(index) {
      this.form.contacts.splice(index, 1);

      this.form.errors.clear();
    },

    contactsChanged(contact, index) {
      this.form.errors.clear([
        `contacts.${index}.first_name`,
        `contacts.${index}.last_name`,
        `contacts.${index}.email`,
        `contacts.${index}.phone`,
        `contacts.${index}.mobile`,
      ]);

      if (!contact.id) {
        this.contactFuzzySearch(contact);
      }
    },

    companyNameChanged() {
      this.companyFuzzySearch(this.form.company_name);
    },

    /* eslint-disable func-names */
    companyFuzzySearch: debounce(function (companyName) {
      if (!companyName || companyName.length === 0) {
        return;
      }

      this.companyFuzzySearching = true;

      this.$http
        .get("/customers/company-fuzzy-search", {
          params: {
            company_name: companyName,
          },
        })
        .then((response) => {
          this.companyFuzzySearchResults = this.$lodash.get(response.body, "data", []);
          this.companyFuzzySearchTotal = this.$lodash.get(response.body, "total");

          this.companyFuzzySearching = false;
        });
    }, 700),

    contactFuzzySearch: debounce(function (contact) {
      if (
        this.$lodash.get(contact, "first_name", "").length === 0 &&
        this.$lodash.get(contact, "last_name", "").length === 0 &&
        this.$lodash.get(contact, "email", "").length === 0 &&
        this.$lodash.get(contact, "phone", "").length === 0 &&
        this.$lodash.get(contact, "mobile", "").length === 0
      ) {
        return;
      }

      this.contactsFuzzySearching = true;

      this.$http
        .get("/contacts/fuzzy-search", {
          params: {
            contact,
          },
        })
        .then((response) => {
          this.contactsFuzzySearchResults = this.$lodash.get(response.body, "data", []);
          this.contactsFuzzySearchTotal = this.$lodash.get(response.body, "total");

          this.contactsFuzzySearching = false;
        });
    }, 700),

    useThisCustomer(customer) {
      EventBus.$emit("job-edit-customer-change", customer);
    },

    addAddress() {
      this.form.addresses.push({
        street: "",
        city: "",
        state: "",
        postcode: "",
        latitude: "",
        longitude: "",
        formatted: "",
        is_postal: false,
        is_active: true,
        is_creating: true,
      });
    },

    addressChanged(address, index) {
      this.form.addresses[index] = address;

      this.form.errors.clear();
    },

    removeAddress(index) {
      this.form.addresses.splice(index, 1);

      this.form.errors.clear();
    },

    primaryContactChanged(checked, index) {
      if (checked) {
        this.form.contacts = this.form.contacts.map((contact) => ({
          ...contact,
          is_primary: false,
        }));

        this.form.contacts[index].is_primary = true;
      } else {
        this.form.contacts[index].is_primary = false;
      }

      this.form.errors.clear("contacts.primary_contact_id");
    },

    postalAddressChanged(checked, index) {
      if (checked) {
        this.form.addresses = this.form.addresses.map((address) => ({
          ...address,
          is_postal: false,
        }));

        this.form.addresses[index].is_postal = true;
      } else {
        this.form.addresses[index].is_postal = false;
      }

      this.form.errors.clear("addresses.postal_address_id");
    },

    contactActiveChanged(checked, index) {
      if (!checked) {
        this.form.contacts[index].is_active = !this.form.contacts[index].is_active;
      }

      this.form.errors.clear(`contacts.${index}.is_active`);
    },

    removeBranch(branch, index) {
      window
        .sweetAlert({
          title: "Confirm to remove this branch?",
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.$http.put(`/customers/${branch.id}/remove-branch`).then((response) => {
              if (response.status === 200) {
                window.toastr.success(`Branch ${branch.company_name} removed`, "Success");
                this.crud.data.branches.splice(index, 1);
              }
            });
          }
        });
    },
  },
};
</script>
